import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminShell } from "../components/AdminShell";
import { api, imageUrl, formatPrice } from "../lib/api";
import { Plus, Edit3, Trash2, Search, Download, Upload, MessageCircle, Radio, Heart } from "lucide-react";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { AdminWhatsappShareModal } from "../components/AdminWhatsappShareModal";
import { EmailStatusBanner } from "../components/EmailStatusBanner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

export default function AdminCars() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [waCar, setWaCar] = useState(null);
  const [waOpen, setWaOpen] = useState(false);
  const [wishlistStats, setWishlistStats] = useState({});

  const fetchCars = async () => {
    setLoading(true);
    try {
      const r = await api.get("/cars", { params: { include_sold: true } });
      setCars(r.data);
    } finally {
      setLoading(false);
    }
  };

  const fetchWishlistStats = async () => {
    try {
      const r = await api.get("/admin/wishlist-stats");
      setWishlistStats(r.data?.stats || {});
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchCars(); fetchWishlistStats(); }, []);

  const shareOnWhatsApp = (c) => {
    setWaCar(c);
    setWaOpen(true);
    // Optimistically stamp last_broadcast_at so the badge updates immediately;
    // the real timestamp is persisted server-side when the endpoint returns.
    api.post(`/cars/${c.id}/mark-broadcast`).then((r) => {
      setCars((cs) => cs.map((x) => x.id === c.id ? { ...x, last_broadcast_at: r.data.last_broadcast_at } : x));
    }).catch(() => {});
  };

  const formatLastBroadcast = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const days = Math.floor(diffMs / (24 * 3600 * 1000));
    const hours = Math.floor(diffMs / (3600 * 1000));
    const mins = Math.floor(diffMs / 60000);
    if (mins < 60) return `${mins || 1} min fa`;
    if (hours < 24) return `${hours}h fa`;
    if (days === 1) return "1 giorno fa";
    if (days < 30) return `${days} giorni fa`;
    return d.toLocaleDateString("it-IT");
  };

  const remove = async (id) => {
    try {
      await api.delete(`/cars/${id}`);
      toast.success("Auto eliminata");
      fetchCars();
    } catch { toast.error("Errore eliminazione"); }
  };

  const toggleSold = async (car) => {
    try {
      await api.put(`/cars/${car.id}`, { is_sold: !car.is_sold });
      toast.success(car.is_sold ? "Auto rimessa in vendita" : "Segnata come venduta");
      fetchCars();
    } catch { toast.error("Errore"); }
  };

  const filtered = cars.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.brand.toLowerCase().includes(q) || c.model.toLowerCase().includes(q);
  });

  const exportJson = () => {
    const data = JSON.stringify({ cars, exported_at: new Date().toISOString() }, null, 2);
    const blob = new Blob([data], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `guzzardella-listino-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Scaricate ${cars.length} auto`);
  };

  const importJson = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const list = Array.isArray(parsed) ? parsed : (parsed.cars || parsed.items || []);
      if (!list.length) { toast.error("Nessuna auto nel file"); return; }
      if (!window.confirm(`Importare ${list.length} auto? Le auto con codice identificativo già presente saranno saltate.`)) return;
      const r = await api.post("/cars/import", { cars: list });
      toast.success(`Importate ${r.data.inserted} auto (${r.data.skipped} saltate)`);
      fetchCars();
    } catch (err) {
      toast.error("File non valido o errore importazione");
    } finally { e.target.value = ""; }
  };

  return (
    <AdminShell title="Parco Auto">
      <EmailStatusBanner />
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            placeholder="Cerca marca o modello..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-midnight-light border-white/10 text-white rounded-sm"
            data-testid="admin-cars-search"
          />
        </div>
        <Link
          to="/admin/cars/new"
          className="btn-gold inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-widest font-semibold"
          data-testid="add-car-btn"
        >
          <Plus size={14} /> Aggiungi Auto
        </Link>
        <button
          onClick={exportJson}
          disabled={cars.length === 0}
          className="inline-flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-widest font-semibold border border-white/20 hover:border-gold text-white/80 hover:text-gold disabled:opacity-40"
          data-testid="export-cars-btn"
          title="Scarica listino come file JSON"
        >
          <Download size={14} /> Esporta
        </button>
        <label
          className="inline-flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-widest font-semibold border border-white/20 hover:border-gold text-white/80 hover:text-gold cursor-pointer"
          data-testid="import-cars-btn"
          title="Carica un file JSON per importare le auto"
        >
          <Upload size={14} /> Importa
          <input type="file" accept="application/json,.json" className="hidden" onChange={importJson} />
        </label>
      </div>

      <div className="bg-midnight-light border border-white/5 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-white/50">Caricamento...</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center">
            <p className="text-white/60 mb-6">Nessuna auto nel parco</p>
            <Link
              to="/admin/cars/new"
              className="btn-gold inline-flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-widest font-semibold"
              data-testid="empty-add-car-btn"
            >
              <Plus size={14} /> Aggiungi la Prima Auto
            </Link>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="cars-table">
            <thead className="bg-white/5 border-b border-white/5">
              <tr className="text-left text-white/50 uppercase text-xs tracking-widest">
                <th className="p-4">Auto</th>
                <th className="p-4 hidden md:table-cell">Anno</th>
                <th className="p-4 hidden md:table-cell">Km</th>
                <th className="p-4">Prezzo</th>
                <th className="p-4">Stato</th>
                <th className="p-4 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.map((c) => (
                <tr key={c.id} className="hover:bg-white/5 transition-colors" data-testid={`admin-car-row-${c.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-28 h-28 bg-midnight shrink-0 overflow-hidden border border-white/10">
                        {c.images?.[0] ? (
                          <img src={imageUrl(c.images[0].storage_path)} alt="" className="w-full h-full object-contain" />
                        ) : null}
                      </div>
                      <div>
                        <p className="font-semibold text-xl leading-tight">{c.brand}</p>
                        <p className="text-white/70 text-base">{c.model}</p>
                        {c.ref_code && (
                          <p className="text-gold text-sm font-mono tracking-wider mt-1" data-testid={`admin-car-ref-${c.id}`}>
                            {c.ref_code}
                          </p>
                        )}
                        {c.last_broadcast_at && (
                          <p className="text-[10px] text-emerald-400 mt-1 inline-flex items-center gap-1" data-testid={`admin-car-last-broadcast-${c.id}`}>
                            <Radio size={10} /> Ultima notifica: {formatLastBroadcast(c.last_broadcast_at)}
                          </p>
                        )}
                        {(wishlistStats[c.id] || 0) > 0 && (
                          <p
                            className="text-[10px] text-gold mt-1 inline-flex items-center gap-1 font-semibold"
                            data-testid={`admin-car-wishlist-count-${c.id}`}
                            title={`${wishlistStats[c.id]} ${wishlistStats[c.id] === 1 ? "persona ha" : "persone hanno"} salvato quest'auto`}
                          >
                            <Heart size={10} fill="currentColor" /> {wishlistStats[c.id]}{" "}
                            {wishlistStats[c.id] === 1 ? "persona interessata" : "persone interessate"}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-4 hidden md:table-cell text-white/70">{c.year}</td>
                  <td className="p-4 hidden md:table-cell text-white/70">{c.km.toLocaleString("it-IT")}</td>
                  <td className="p-4 text-gold font-semibold">{formatPrice(c.price)}</td>
                  <td className="p-4">
                    <button
                      onClick={() => toggleSold(c)}
                      className={`text-[10px] uppercase px-2 py-1 rounded-sm ${
                        c.is_sold ? "bg-white/10 text-white/60" : "bg-emerald-500/20 text-emerald-300"
                      }`}
                      data-testid={`toggle-sold-${c.id}`}
                    >
                      {c.is_sold ? "Venduta" : "Disponibile"}
                    </button>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => shareOnWhatsApp(c)}
                        className="p-2 hover:bg-emerald-500 hover:text-white rounded-sm transition-colors text-emerald-500"
                        data-testid={`share-whatsapp-${c.id}`}
                        title="Condividi su WhatsApp"
                        aria-label="Condividi su WhatsApp"
                      >
                        <MessageCircle size={14} />
                      </button>
                      <Link
                        to={`/admin/cars/${c.id}/edit`}
                        className="p-2 hover:bg-gold hover:text-midnight rounded-sm transition-colors"
                        data-testid={`edit-car-${c.id}`}
                      >
                        <Edit3 size={14} />
                      </Link>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button
                            className="p-2 hover:bg-destructive hover:text-white rounded-sm transition-colors text-destructive"
                            data-testid={`delete-car-${c.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Eliminare quest'auto?</AlertDialogTitle>
                            <AlertDialogDescription>
                              L'operazione è irreversibile. Verranno rimosse anche tutte le foto.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annulla</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => remove(c.id)}
                              className="bg-destructive text-white"
                              data-testid={`confirm-delete-${c.id}`}
                            >
                              Elimina
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AdminWhatsappShareModal
        car={waCar}
        open={waOpen}
        onClose={() => setWaOpen(false)}
      />
    </AdminShell>
  );
}
